// LibraryPickerDialog — lists the current user's library templates filtered
// by product_type. Clicking a template opens the appropriate editor PRE-LOADED
// with that template's doorData (NO ItemQuote is created yet); the user can
// review, edit, and Save (creates the ItemQuote) or close without saving
// (nothing is created) — exactly like "Add Predefined Window/Door".
//
// Inline management:
//   - Pencil icon  → row turns into an editable name field
//       Enter   → PATCH the new name
//       Esc     → cancel (no save)
//       blur    → commit (modern Drive-style; no-ops if name unchanged or blank)
//   - Trash icon  → confirm dialog → DELETE the template
// Both actions dispatch 'library-templates-updated' so the badge / popup item
// visibility refresh immediately. Owner-only on the backend.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemButton, ListItemText,
  IconButton, TextField, Button, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authFetch, authFetchRequest } from '../../utils/api';
import { useLibraryTemplates } from '../../utils/useLibraryTemplates';

// Which editor to open per product_type. Extend as more product editors come online
// (e.g. entry doors → '/entrydooreticom', fiberglass → ...).
const EDITOR_PATH_BY_PRODUCT = {
  window: '/windows/wint/w1',
  patiodoors: '/patiodoors/pdt/pd1',
  pleatedscreens: '/pleatedscreenn',
};

const LibraryPickerDialog = ({
  open, productType, quoteId, fkCustomerId, fkUserId,
  onClose,
}) => {
  const { items, loaded, refresh } = useLibraryTemplates(productType);
  const [opening, setOpening] = useState(false);
  const [busy, setBusy] = useState(false);          // covers rename / delete
  const [editing, setEditing] = useState(null);     // { id, name } when inline-renaming
  const [deleting, setDeleting] = useState(null);   // { id, name } when confirming delete
  const navigate = useNavigate();

  // Re-fetch each time the dialog opens (in case the list changed while closed).
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handlePick = async (tpl) => {
    if (opening || busy) return;
    const editorPath = EDITOR_PATH_BY_PRODUCT[tpl.product_type];
    if (!editorPath) {
      alert(`No editor wired for product type "${tpl.product_type}" yet.`);
      return;
    }
    setOpening(true);
    try {
      const full = await authFetch(`/library-templates/${tpl.id}`);
      if (!full?.doorData) throw new Error('template missing doorData');
      // Navigate to the editor with the template's doorData pre-loaded via
      // location.state. The editor treats it like a brand-new item (no
      // quoteItemId), so Save creates a fresh ItemQuote and closing creates
      // nothing — matching the "Add Predefined …" UX.
      navigate(editorPath, {
        state: {
          quoteId,
          fkCustomerId,
          fkUserId,
          viewItem: false,
          libraryDoorData: full.doorData,
        },
      });
      onClose();
    } catch (err) {
      console.error('Open library template failed:', err);
      alert('Failed to open template: ' + (err?.message || 'unknown error'));
    } finally {
      setOpening(false);
    }
  };

  const commitRename = async (originalName) => {
    if (!editing) return;
    const newName = editing.name.trim();
    // No-op if blank or unchanged — silently close the editor.
    if (!newName || newName === originalName) { setEditing(null); return; }
    setBusy(true);
    try {
      await authFetchRequest(`/library-templates/${editing.id}`, { name: newName }, 'PATCH');
      window.dispatchEvent(new Event('library-templates-updated'));
      setEditing(null);
    } catch (err) {
      console.error('Rename failed:', err);
      alert('Rename failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await authFetchRequest(`/library-templates/${deleting.id}`, null, 'DELETE');
      window.dispatchEvent(new Event('library-templates-updated'));
      setDeleting(null);
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add predefined from My Library</DialogTitle>
        <DialogContent dividers>
          {!loaded ? (
            <CircularProgress size={20} />
          ) : items.length === 0 ? (
            <p>No saved templates for this product type yet.</p>
          ) : (
            <List dense>
              {items.map((t) => {
                const isEditing = editing?.id === t.id;
                return (
                  <ListItem
                    key={t.id}
                    disablePadding
                    secondaryAction={!isEditing && (
                      <>
                        <IconButton
                          edge="end" size="small" aria-label="rename"
                          disabled={opening || busy}
                          onClick={(e) => { e.stopPropagation(); setEditing({ id: t.id, name: t.name }); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end" size="small" aria-label="delete"
                          disabled={opening || busy}
                          onClick={(e) => { e.stopPropagation(); setDeleting({ id: t.id, name: t.name }); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  >
                    {isEditing ? (
                      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 16px', width: '100%' }}>
                        <TextField
                          autoFocus fullWidth size="small"
                          value={editing.name}
                          disabled={busy}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(t.name);
                            else if (e.key === 'Escape') setEditing(null);
                          }}
                          onBlur={() => commitRename(t.name)}
                        />
                      </div>
                    ) : (
                      <ListItemButton disabled={opening || busy} onClick={() => handlePick(t)}>
                        <ListItemText primary={t.name} secondary={t.product_type} />
                      </ListItemButton>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={opening || busy}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — standard pattern. */}
      <Dialog open={!!deleting} onClose={() => !busy && setDeleting(null)} maxWidth="xs">
        <DialogTitle>Delete from library?</DialogTitle>
        <DialogContent>
          <p>Delete <strong>"{deleting?.name}"</strong> from your library?</p>
          <p style={{ fontSize: '0.85em', color: '#666', margin: 0 }}>
            Items already added to quotes from this template are not affected.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LibraryPickerDialog;
